import { Alert, Button, Modal } from '@catering/ui';
import { type JSX, useState } from 'react';

import { type ContractDetail } from '../../../contracts/api';
import { hasPermission } from '../../../auth/has-permission';
import type { CurrentUser } from '../../../auth/use-current-user';
import { canTransition } from './contract-transitions';

export interface ContractTransitionButtonsProps {
  contract: ContractDetail;
  currentUser: CurrentUser | undefined;
  onActivate: () => void;
  onPause: (reason: string | null) => void;
  onTerminate: (reason: string) => void;
  activating: boolean;
  pausing: boolean;
  terminating: boolean;
}

/**
 * Each of the three transition buttons is evaluated against its own
 * permission and its own state-machine eligibility independently: a button
 * the API would reject for lack of permission is not rendered at all, and
 * one the contract state does not allow is rendered disabled.
 */
export function ContractTransitionButtons({
  contract,
  currentUser,
  onActivate,
  onPause,
  onTerminate,
  activating,
  pausing,
  terminating,
}: ContractTransitionButtonsProps): JSX.Element {
  const [terminateOpen, setTerminateOpen] = useState(false);

  const canActivateState = canTransition(contract.status, 'ACTIVE');
  const canPauseState = canTransition(contract.status, 'PAUSED');
  const canTerminateState = canTransition(contract.status, 'TERMINATED');

  const hasActivatePermission = hasPermission(currentUser, 'contract.activate');
  const hasPausePermission = hasPermission(currentUser, 'contract.pause');
  const hasTerminatePermission = hasPermission(currentUser, 'contract.terminate');

  if (!hasActivatePermission && !hasPausePermission && !hasTerminatePermission) {
    return <></>;
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap gap-3">
        {hasActivatePermission ? (
          <Button loading={activating} disabled={!canActivateState} onClick={onActivate}>
            Activar
          </Button>
        ) : null}
        {hasPausePermission ? (
          <Button
            variant="secondary"
            loading={pausing}
            disabled={!canPauseState}
            onClick={() => {
              if (window.confirm('¿Pausar este contrato? Mientras esté pausado no se pueden cargar pedidos.')) {
                onPause(null);
              }
            }}
          >
            Pausar
          </Button>
        ) : null}
        {hasTerminatePermission ? (
          <Button
            variant="destructive"
            loading={terminating}
            disabled={!canTerminateState}
            onClick={() => setTerminateOpen(true)}
          >
            Dar de baja
          </Button>
        ) : null}
      </div>

      {terminateOpen ? (
        <TerminateDialog
          submitting={terminating}
          onCancel={() => setTerminateOpen(false)}
          onConfirm={(reason) => {
            onTerminate(reason);
            setTerminateOpen(false);
          }}
        />
      ) : null}
    </div>
  );
}

interface TerminateDialogProps {
  submitting: boolean;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
}

function TerminateDialog({ submitting, onCancel, onConfirm }: TerminateDialogProps): JSX.Element {
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);
  const trimmed = reason.trim();
  const showError = touched && trimmed === '';

  function handleSubmit(): void {
    setTouched(true);

    if (trimmed === '') {
      return;
    }

    onConfirm(trimmed);
  }

  return (
    <Modal open onClose={onCancel} title="Dar de baja el contrato" className="max-w-md">
      <div className="flex flex-col gap-3">
        {showError ? <Alert tone="danger">El motivo es obligatorio</Alert> : null}

        <label htmlFor="terminate-reason" className="text-sm font-medium text-slate-900">
          Motivo
          <span aria-hidden="true" className="ml-0.5 text-red-600">
            *
          </span>
        </label>
        <textarea
          id="terminate-reason"
          autoFocus
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          className={`min-h-24 rounded-md border px-3 py-2 text-sm ${showError ? 'border-red-500' : 'border-slate-300'}`}
        />

        <div className="mt-2 flex items-center gap-3">
          <Button type="button" variant="destructive" loading={submitting} onClick={handleSubmit}>
            Dar de baja
          </Button>
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancelar
          </Button>
        </div>
      </div>
    </Modal>
  );
}
